import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const OverviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
`;

const RecentActivity = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ActivityTitle = styled.h3`
  margin-bottom: 1rem;
  color: white;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActivityText = styled.div`
  color: rgba(255, 255, 255, 0.9);
`;

const ActivityTime = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
`;

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api/v1';

function DashboardOverview({ showMessage }) {
  const [stats, setStats] = useState({
    totalStudents: 0,
    unverifiedStudents: 0,
    pendingAdditionalStudents: 0,
    rejectedStudents: 0,
    verifiedStudents: 0,
    scheduledLiveSessions: 0,
    unresolvedInquiries: 0,
    totalFeedback: 0
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get auth token
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Initialize default stats
      const defaultStats = {
        totalStudents: 0,
        unverifiedStudents: 0,
        pendingAdditionalStudents: 0,
        rejectedStudents: 0,
        verifiedStudents: 0,
        scheduledLiveSessions: 0,
        unresolvedInquiries: 0,
        totalFeedback: 0
      };
      
      let statsData = { ...defaultStats };
      let recentInquiriesData = [];
      
      // Fetch students data (requires auth)
      try {
        const studentsResponse = await fetch(`${API_BASE_URL}/admin/students/stats`, {
          headers
        });
        
        if (studentsResponse.ok) {
          const studentsData = await studentsResponse.json();
          if (studentsData.success) {
            statsData.totalStudents = studentsData.data.totalStudents || 0;
            statsData.unverifiedStudents = studentsData.data.unverifiedStudents || 0;
            statsData.pendingAdditionalStudents = studentsData.data.pendingAdditionalStudents || 0;
            statsData.rejectedStudents = studentsData.data.rejectedStudents || 0;
            statsData.verifiedStudents = studentsData.data.verifiedStudents || 0;
          }
        }
      } catch (error) {
        console.warn('Students stats endpoint not available:', error.message);
      }
      
      // Fetch live sessions data (public)
      try {
        const liveSessionsResponse = await fetch(`${API_BASE_URL}/live-sessions/stats`);
        if (liveSessionsResponse.ok) {
          const liveSessionsData = await liveSessionsResponse.json();
          if (liveSessionsData.success) {
            statsData.scheduledLiveSessions = liveSessionsData.data.scheduledSessions || 0;
          }
        }
      } catch (error) {
        console.warn('Live sessions stats endpoint not available:', error.message);
      }
      
      // Fetch inquiries data (public)
      try {
        const inquiriesResponse = await fetch(`${API_BASE_URL}/stats`);
        if (inquiriesResponse.ok) {
          const inquiriesData = await inquiriesResponse.json();
          if (inquiriesData.success) {
            statsData.unresolvedInquiries = inquiriesData.data.unresolvedInquiries || 0;
            recentInquiriesData = inquiriesData.data.recentInquiries || [];
          }
        }
      } catch (error) {
        console.warn('Inquiries stats endpoint not available:', error.message);
      }
      
      // Fetch feedback data (requires auth)
      try {
        const feedbackResponse = await fetch(`${API_BASE_URL}/admin/feedback/stats`, {
          headers
        });
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          if (feedbackData.success) {
            statsData.totalFeedback = feedbackData.data.totalFeedback || 0;
          }
        }
      } catch (error) {
        console.warn('Feedback stats endpoint not available:', error.message);
      }
      
      setStats(statsData);
      setRecentInquiries(recentInquiriesData);
    } catch (error) {
      showMessage('Error fetching dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      <OverviewContainer>
        <StatCard>
          <StatNumber>{stats.totalStudents}</StatNumber>
          <StatLabel>Total Students</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.unverifiedStudents}</StatNumber>
          <StatLabel>Unverified Students</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.pendingAdditionalStudents}</StatNumber>
          <StatLabel>Pending Additional</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.rejectedStudents}</StatNumber>
          <StatLabel>Rejected Students</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.verifiedStudents}</StatNumber>
          <StatLabel>Verified Students</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.scheduledLiveSessions}</StatNumber>
          <StatLabel>Scheduled Live Sessions</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.unresolvedInquiries}</StatNumber>
          <StatLabel>Unresolved Inquiries</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.totalFeedback}</StatNumber>
          <StatLabel>Total Feedback</StatLabel>
        </StatCard>
      </OverviewContainer>

      <RecentActivity>
        <ActivityTitle>Recent Inquiries</ActivityTitle>
        <ActivityList>
          {recentInquiries.length > 0 ? (
            recentInquiries.map(inquiry => (
              <ActivityItem key={inquiry.id}>
                <ActivityText>
                  <strong>{inquiry.subject || 'No subject'}</strong> - {inquiry.status}
                  {inquiry.student_name && ` (${inquiry.student_name})`}
                </ActivityText>
                <ActivityTime>{formatTime(inquiry.created_at)}</ActivityTime>
              </ActivityItem>
            ))
          ) : (
            <ActivityItem>
              <ActivityText>No recent inquiries</ActivityText>
            </ActivityItem>
          )}
        </ActivityList>
      </RecentActivity>
    </div>
  );
}

export default DashboardOverview;
